import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import {
  ChevronDown,
  ChevronUp,
  Save,
  Plus,
  Trash2,
  User,
  Heart,
  Users as UsersIcon,
  Baby,
  Shield,
  HardHat,
  Stethoscope,
  Landmark,
  TrendingUp,
  Gem,
  KeyRound,
  Share2,
  Flower,
  PencilLine,
  Scale,
} from "lucide-react";

// Default empty profile shape, kept in sync with backend `_empty_ice_profile`.
const EMPTY = {
  personal: {},
  marital: {},
  divorce: {},
  children: [],
  guardians: [],
  domestic_staff: [],
  pro_contacts: {},
  medical: {},
  bank_accounts: [],
  investments: [],
  valuables: [],
  credentials_location: {},
  social_accounts: [],
  funeral_wishes: "",
  additional_notes: "",
  heir_overrides: {},
};

const cls = (...xs) => xs.filter(Boolean).join(" ");

// Lightweight inline validators. Empty input is always valid (fields are optional).
const RE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Loose phone: 7-20 digits, optional leading +, may contain spaces / dashes / dots / parens
const RE_PHONE = /^\+?[\d().\-\s]{7,20}$/;

function validateValue(kind, value) {
  if (value === undefined || value === null || value === "") return null;
  const v = String(value).trim();
  if (v === "") return null;
  if (kind === "email") return RE_EMAIL.test(v) ? null : "invalid_email";
  if (kind === "phone") return RE_PHONE.test(v) ? null : "invalid_phone";
  return null;
}

function Section({ id, icon: Icon, title, hint, open, onToggle, children, testid }) {
  return (
    <div className="glass-card rounded-md overflow-hidden" data-testid={testid}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 sm:px-5 py-3.5 text-left hover:bg-gold/5 transition"
        aria-expanded={open}
        data-testid={`${testid}-toggle`}
      >
        {Icon && <Icon className="w-4 h-4 text-gold shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="font-display text-gold-light">{title}</div>
          {hint && <div className="text-[11px] text-faded mt-0.5">{hint}</div>}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gold" /> : <ChevronDown className="w-4 h-4 text-faded" />}
      </button>
      {open && <div className="px-4 sm:px-5 pb-5 pt-1 space-y-4 border-t border-gold-subtle">{children}</div>}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="font-label text-faded text-[11px] block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, testid, type = "text", validate }) {
  const { t } = useI18n();
  const errKey = validate ? validateValue(validate, value) : null;
  return (
    <>
      <input
        type={type}
        className={cls("input-field", errKey && "input-field-error")}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || ""}
        data-testid={testid}
        aria-invalid={errKey ? "true" : undefined}
      />
      {errKey && (
        <p className="text-[11px] text-red-300/90 mt-1" data-testid={`${testid}-error`}>
          ✗ {t(`val_${errKey}`)}
        </p>
      )}
    </>
  );
}

function TextArea({ value, onChange, placeholder, testid, rows = 3 }) {
  return (
    <textarea
      rows={rows}
      className="input-field"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder || ""}
      data-testid={testid}
    />
  );
}

/* Repeating-row primitive used by children/guardians/domestic-staff/bank/investments/etc. */
function RepeaterSection({ rows, onChange, fields, addLabel, emptyLabel, testid }) {
  const setRow = (idx, key, v) => {
    const next = [...rows];
    next[idx] = { ...next[idx], [key]: v };
    onChange(next);
  };
  const addRow = () => onChange([...rows, {}]);
  const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3">
      {rows.length === 0 ? (
        <p className="text-faded text-xs italic">{emptyLabel}</p>
      ) : (
        rows.map((row, idx) => (
          <div
            key={idx}
            className="relative border border-gold-subtle rounded-md p-3 bg-white/[0.02] space-y-2"
            data-testid={`${testid}-row-${idx}`}
          >
            <button
              type="button"
              onClick={() => removeRow(idx)}
              className="absolute top-2 right-2 text-faded hover:text-red-400 transition"
              aria-label="remove"
              data-testid={`${testid}-remove-${idx}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {fields.map((f) => (
                <Field key={f.key} label={f.label}>
                  {f.textarea ? (
                    <TextArea
                      value={row[f.key]}
                      onChange={(v) => setRow(idx, f.key, v)}
                      placeholder={f.placeholder}
                      testid={`${testid}-${f.key}-${idx}`}
                      rows={2}
                    />
                  ) : (
                    <TextInput
                      value={row[f.key]}
                      onChange={(v) => setRow(idx, f.key, v)}
                      placeholder={f.placeholder}
                      testid={`${testid}-${f.key}-${idx}`}
                    />
                  )}
                </Field>
              ))}
            </div>
          </div>
        ))
      )}
      <button
        type="button"
        onClick={addRow}
        className="text-xs px-3 py-1.5 rounded-md border border-gold-subtle text-gold-light hover:bg-gold/10 transition flex items-center gap-1.5"
        data-testid={`${testid}-add`}
      >
        <Plus className="w-3.5 h-3.5" /> {addLabel}
      </button>
    </div>
  );
}

export default function IceProfileEditor({ heirs = [], onSaved }) {
  const { t } = useI18n();
  const [profile, setProfile] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openSection, setOpenSection] = useState("personal");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/ice-profile");
      setProfile({ ...EMPTY, ...(r.data || {}) });
    } catch {
      /* empty profile if request fails */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setSection = (key, value) => setProfile((p) => ({ ...p, [key]: value }));
  const setNested = (sectionKey, fieldKey, value) =>
    setProfile((p) => ({ ...p, [sectionKey]: { ...(p[sectionKey] || {}), [fieldKey]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/ice-profile", profile);
      toast.success(t("saved"));
      onSaved && onSaved();
    } catch (err) {
      // 403 → paywall modal handled globally
      if (err?.response?.status !== 403) {
        toast.error(err?.response?.data?.detail || "Error");
      }
    } finally {
      setSaving(false);
    }
  };

  const toggle = (key) => setOpenSection((cur) => (cur === key ? null : key));

  /* Heir overrides — autofill row per heir */
  const setHeirOverride = (heirId, key, value) => {
    const current = profile.heir_overrides?.[heirId] || {};
    setProfile((p) => ({
      ...p,
      heir_overrides: { ...(p.heir_overrides || {}), [heirId]: { ...current, [key]: value } },
    }));
  };

  if (loading) {
    return <div className="text-faded text-sm py-6" data-testid="ice-editor-loading">{t("loading") || "…"}</div>;
  }

  return (
    <div className="space-y-3" data-testid="ice-profile-editor">
      {/* Section 2 — Personal */}
      <Section id="personal" icon={User} title={t("ice_sec_personal")} hint={t("ice_sec_personal_hint")}
        open={openSection === "personal"} onToggle={() => toggle("personal")} testid="ice-sec-personal">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label={t("ice_p_full_name")}>
            <TextInput value={profile.personal?.full_name} onChange={(v) => setNested("personal", "full_name", v)} testid="ice-p-full_name" />
          </Field>
          <Field label={t("ice_p_id_number")}>
            <TextInput value={profile.personal?.id_number} onChange={(v) => setNested("personal", "id_number", v)} testid="ice-p-id_number" />
          </Field>
          <Field label={t("ice_p_phone_home")}>
            <TextInput value={profile.personal?.phone_home} onChange={(v) => setNested("personal", "phone_home", v)} testid="ice-p-phone_home" type="tel" validate="phone" />
          </Field>
          <Field label={t("ice_p_phone_work")}>
            <TextInput value={profile.personal?.phone_work} onChange={(v) => setNested("personal", "phone_work", v)} testid="ice-p-phone_work" type="tel" validate="phone" />
          </Field>
          <Field label={t("ice_p_passport")}>
            <TextInput value={profile.personal?.passport} onChange={(v) => setNested("personal", "passport", v)} testid="ice-p-passport" />
          </Field>
          <Field label={t("ice_p_nationality")}>
            <TextInput value={profile.personal?.nationality} onChange={(v) => setNested("personal", "nationality", v)} testid="ice-p-nationality" />
          </Field>
          <Field label={t("ice_p_residence")}>
            <TextInput value={profile.personal?.residence} onChange={(v) => setNested("personal", "residence", v)} testid="ice-p-residence" />
          </Field>
          <Field label={t("ice_p_email")}>
            <TextInput value={profile.personal?.email} onChange={(v) => setNested("personal", "email", v)} testid="ice-p-email" type="email" validate="email" />
          </Field>
        </div>
      </Section>

      {/* Section 3 — Marital / spouse */}
      <Section id="marital" icon={Heart} title={t("ice_sec_marital")} hint={t("ice_sec_marital_hint")}
        open={openSection === "marital"} onToggle={() => toggle("marital")} testid="ice-sec-marital">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label={t("ice_m_status")}>
            <TextInput value={profile.marital?.status} onChange={(v) => setNested("marital", "status", v)} placeholder={t("ice_m_status_ph")} testid="ice-m-status" />
          </Field>
          <Field label={t("ice_m_spouse_name")}>
            <TextInput value={profile.marital?.spouse_name} onChange={(v) => setNested("marital", "spouse_name", v)} testid="ice-m-spouse_name" />
          </Field>
          <Field label={t("ice_m_spouse_id")}>
            <TextInput value={profile.marital?.spouse_id} onChange={(v) => setNested("marital", "spouse_id", v)} testid="ice-m-spouse_id" />
          </Field>
          <Field label={t("ice_m_spouse_phone")}>
            <TextInput value={profile.marital?.spouse_phone} onChange={(v) => setNested("marital", "spouse_phone", v)} type="tel" testid="ice-m-spouse_phone" validate="phone" />
          </Field>
          <Field label={t("ice_m_spouse_email")}>
            <TextInput value={profile.marital?.spouse_email} onChange={(v) => setNested("marital", "spouse_email", v)} type="email" testid="ice-m-spouse_email" validate="email" />
          </Field>
        </div>
      </Section>

      {/* Section 4 — Previous divorce reference */}
      <Section id="divorce" icon={Scale} title={t("ice_sec_divorce")} hint={t("ice_sec_divorce_hint")}
        open={openSection === "divorce"} onToggle={() => toggle("divorce")} testid="ice-sec-divorce">
        <Field label={t("ice_d_agreement_location")}>
          <TextArea value={profile.divorce?.agreement_location} onChange={(v) => setNested("divorce", "agreement_location", v)} rows={2}
            placeholder={t("ice_d_agreement_ph")} testid="ice-d-agreement_location" />
        </Field>
        <Field label={t("ice_d_notes")}>
          <TextArea value={profile.divorce?.notes} onChange={(v) => setNested("divorce", "notes", v)} rows={2} testid="ice-d-notes" />
        </Field>
      </Section>

      {/* Section 5 — Heir overrides (autofilled from /heirs) */}
      <Section id="heirs" icon={UsersIcon} title={t("ice_sec_heir_overrides")} hint={t("ice_sec_heir_overrides_hint")}
        open={openSection === "heirs"} onToggle={() => toggle("heirs")} testid="ice-sec-heir_overrides">
        {heirs.length === 0 ? (
          <p className="text-faded text-xs italic">{t("ice_no_heirs_yet")}</p>
        ) : (
          <div className="space-y-3">
            {heirs.map((h) => {
              const ov = profile.heir_overrides?.[h.heir_id] || {};
              return (
                <div key={h.heir_id} className="border border-gold-subtle rounded-md p-3 bg-white/[0.02]"
                  data-testid={`ice-heir-${h.heir_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-gold-light">
                      {h.name} <span className="text-faded text-xs">· {h.relationship || ""}</span>
                    </div>
                    <label className="flex items-center gap-2 text-xs text-muted cursor-pointer">
                      <input
                        type="checkbox"
                        checked={ov.notify ?? true}
                        onChange={(e) => setHeirOverride(h.heir_id, "notify", e.target.checked)}
                        data-testid={`ice-heir-notify-${h.heir_id}`}
                      />
                      {t("ice_h_notify")}
                    </label>
                  </div>
                  <Field label={t("ice_h_bequest")}>
                    <TextArea
                      value={ov.bequest}
                      onChange={(v) => setHeirOverride(h.heir_id, "bequest", v)}
                      rows={2}
                      placeholder={t("ice_h_bequest_ph")}
                      testid={`ice-heir-bequest-${h.heir_id}`}
                    />
                  </Field>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* Section 6 — Children */}
      <Section id="children" icon={Baby} title={t("ice_sec_children")} hint={t("ice_sec_children_hint")}
        open={openSection === "children"} onToggle={() => toggle("children")} testid="ice-sec-children">
        <RepeaterSection
          rows={profile.children || []}
          onChange={(rows) => setSection("children", rows)}
          fields={[
            { key: "name", label: t("ice_c_name") },
            { key: "contact", label: t("ice_c_contact"), placeholder: t("ice_c_contact_ph") },
          ]}
          addLabel={t("ice_c_add")}
          emptyLabel={t("ice_c_empty")}
          testid="ice-children"
        />
      </Section>

      {/* Section 7 — Children's guardians */}
      <Section id="guardians" icon={Shield} title={t("ice_sec_guardians")} hint={t("ice_sec_guardians_hint")}
        open={openSection === "guardians"} onToggle={() => toggle("guardians")} testid="ice-sec-guardians">
        <RepeaterSection
          rows={profile.guardians || []}
          onChange={(rows) => setSection("guardians", rows)}
          fields={[
            { key: "name", label: t("ice_g_name") },
            { key: "contact", label: t("ice_g_contact") },
          ]}
          addLabel={t("ice_g_add")}
          emptyLabel={t("ice_g_empty")}
          testid="ice-guardians"
        />
      </Section>

      {/* Section 8 — Domestic staff */}
      <Section id="staff" icon={HardHat} title={t("ice_sec_staff")} hint={t("ice_sec_staff_hint")}
        open={openSection === "staff"} onToggle={() => toggle("staff")} testid="ice-sec-staff">
        <RepeaterSection
          rows={profile.domestic_staff || []}
          onChange={(rows) => setSection("domestic_staff", rows)}
          fields={[
            { key: "name", label: t("ice_s_name") },
            { key: "contact", label: t("ice_s_contact"), placeholder: t("ice_s_contact_ph") },
          ]}
          addLabel={t("ice_s_add")}
          emptyLabel={t("ice_s_empty")}
          testid="ice-staff"
        />
      </Section>

      {/* Section 9 — Professional contacts */}
      <Section id="pro" icon={Stethoscope} title={t("ice_sec_pro_contacts")} hint={t("ice_sec_pro_contacts_hint")}
        open={openSection === "pro"} onToggle={() => toggle("pro")} testid="ice-sec-pro">
        {[
          { k: "doctor", label: t("ice_pro_doctor") },
          { k: "lawyer", label: t("ice_pro_lawyer") },
          { k: "financial_advisor", label: t("ice_pro_advisor") },
        ].map((p) => (
          <div key={p.k} className="border border-gold-subtle rounded-md p-3 bg-white/[0.02] mb-2 space-y-2">
            <div className="text-gold-light text-sm">{p.label}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label={t("ice_pro_name")}>
                <TextInput
                  value={profile.pro_contacts?.[p.k]?.name}
                  onChange={(v) => setNested("pro_contacts", p.k, { ...(profile.pro_contacts?.[p.k] || {}), name: v })}
                  testid={`ice-pro-${p.k}-name`}
                />
              </Field>
              <Field label={t("ice_pro_contact")}>
                <TextInput
                  value={profile.pro_contacts?.[p.k]?.contact}
                  onChange={(v) => setNested("pro_contacts", p.k, { ...(profile.pro_contacts?.[p.k] || {}), contact: v })}
                  testid={`ice-pro-${p.k}-contact`}
                />
              </Field>
              <Field label={t("ice_pro_email")}>
                <TextInput
                  value={profile.pro_contacts?.[p.k]?.email}
                  onChange={(v) => setNested("pro_contacts", p.k, { ...(profile.pro_contacts?.[p.k] || {}), email: v })}
                  type="email"
                  testid={`ice-pro-${p.k}-email`}
                  validate="email"
                />
              </Field>
            </div>
          </div>
        ))}
      </Section>

      {/* Section 10 — Medical */}
      <Section id="medical" icon={Stethoscope} title={t("ice_sec_medical")} hint={t("ice_sec_medical_hint")}
        open={openSection === "medical"} onToggle={() => toggle("medical")} testid="ice-sec-medical">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label={t("ice_med_insurance")}>
            <TextInput value={profile.medical?.insurance} onChange={(v) => setNested("medical", "insurance", v)} testid="ice-med-insurance" />
          </Field>
          <Field label={t("ice_med_member_id")}>
            <TextInput value={profile.medical?.member_id} onChange={(v) => setNested("medical", "member_id", v)} testid="ice-med-member_id" />
          </Field>
          <Field label={t("ice_med_blood_type")}>
            <TextInput value={profile.medical?.blood_type} onChange={(v) => setNested("medical", "blood_type", v)} placeholder="A+, O-, AB+…" testid="ice-med-blood_type" />
          </Field>
          <Field label={t("ice_med_living_will")}>
            <select
              className="input-field"
              value={profile.medical?.living_will || "unknown"}
              onChange={(e) => setNested("medical", "living_will", e.target.value)}
              data-testid="ice-med-living_will"
            >
              <option value="yes">{t("yes")}</option>
              <option value="no">{t("no")}</option>
              <option value="unknown">{t("ice_med_living_will_unknown")}</option>
            </select>
          </Field>
        </div>
      </Section>

      {/* Section 11 — Bank accounts */}
      <Section id="banks" icon={Landmark} title={t("ice_sec_banks")} hint={t("ice_sec_banks_hint")}
        open={openSection === "banks"} onToggle={() => toggle("banks")} testid="ice-sec-banks">
        <RepeaterSection
          rows={profile.bank_accounts || []}
          onChange={(rows) => setSection("bank_accounts", rows)}
          fields={[
            { key: "entity", label: t("ice_b_entity"), placeholder: "BBVA, ING, Santander…" },
            { key: "reference", label: t("ice_b_reference"), placeholder: "Últimos 4 dígitos · ES**…1234" },
            { key: "auto_payments", label: t("ice_b_auto_payments"), placeholder: t("ice_b_auto_payments_ph"), textarea: true },
          ]}
          addLabel={t("ice_b_add")}
          emptyLabel={t("ice_b_empty")}
          testid="ice-banks"
        />
        <p className="text-[11px] text-faded italic mt-2">{t("ice_b_no_creds")}</p>
      </Section>

      {/* Section 12 — Investments */}
      <Section id="investments" icon={TrendingUp} title={t("ice_sec_investments")} hint={t("ice_sec_investments_hint")}
        open={openSection === "investments"} onToggle={() => toggle("investments")} testid="ice-sec-investments">
        <RepeaterSection
          rows={profile.investments || []}
          onChange={(rows) => setSection("investments", rows)}
          fields={[
            { key: "type", label: t("ice_inv_type"), placeholder: t("ice_inv_type_ph") },
            { key: "platform", label: t("ice_inv_platform"), placeholder: "MyInvestor, Coinbase, Idealista…" },
            { key: "reference", label: t("ice_inv_reference") },
          ]}
          addLabel={t("ice_inv_add")}
          emptyLabel={t("ice_inv_empty")}
          testid="ice-investments"
        />
        <p className="text-[11px] text-faded italic mt-2">{t("ice_b_no_creds")}</p>
      </Section>

      {/* Section 13 — Valuables, jewelry */}
      <Section id="valuables" icon={Gem} title={t("ice_sec_valuables")} hint={t("ice_sec_valuables_hint")}
        open={openSection === "valuables"} onToggle={() => toggle("valuables")} testid="ice-sec-valuables">
        <RepeaterSection
          rows={profile.valuables || []}
          onChange={(rows) => setSection("valuables", rows)}
          fields={[
            { key: "description", label: t("ice_v_description"), placeholder: t("ice_v_description_ph"), textarea: true },
            { key: "estimated_value", label: t("ice_v_estimated_value"), placeholder: "€5.000…" },
            { key: "location", label: t("ice_v_location") },
          ]}
          addLabel={t("ice_v_add")}
          emptyLabel={t("ice_v_empty")}
          testid="ice-valuables"
        />
      </Section>

      {/* Section 14 — Credentials location (NO passwords) */}
      <Section id="creds" icon={KeyRound} title={t("ice_sec_credentials")} hint={t("ice_sec_credentials_hint")}
        open={openSection === "creds"} onToggle={() => toggle("creds")} testid="ice-sec-credentials">
        <Field label={t("ice_cred_type")}>
          <select
            className="input-field"
            value={profile.credentials_location?.type || ""}
            onChange={(e) => setNested("credentials_location", "type", e.target.value)}
            data-testid="ice-cred-type"
          >
            <option value="">{t("ice_cred_type_choose")}</option>
            <option value="password_manager">{t("ice_cred_type_pm")}</option>
            <option value="browser">{t("ice_cred_type_browser")}</option>
            <option value="paper">{t("ice_cred_type_paper")}</option>
            <option value="memory">{t("ice_cred_type_memory")}</option>
            <option value="mixed">{t("ice_cred_type_mixed")}</option>
          </select>
        </Field>
        <Field label={t("ice_cred_where")}>
          <TextArea
            value={profile.credentials_location?.where}
            onChange={(v) => setNested("credentials_location", "where", v)}
            rows={3}
            placeholder={t("ice_cred_where_ph")}
            testid="ice-cred-where"
          />
        </Field>
        <Field label={t("ice_cred_how_to_access")}>
          <TextArea
            value={profile.credentials_location?.how_to_access}
            onChange={(v) => setNested("credentials_location", "how_to_access", v)}
            rows={3}
            placeholder={t("ice_cred_how_to_access_ph")}
            testid="ice-cred-how_to_access"
          />
        </Field>
        <div className="bg-red-500/5 border border-red-500/30 rounded-md p-3 text-[11px] text-red-200/90">
          {t("ice_cred_warning")}
        </div>
      </Section>

      {/* Section 15 — Social accounts */}
      <Section id="social" icon={Share2} title={t("ice_sec_social")} hint={t("ice_sec_social_hint")}
        open={openSection === "social"} onToggle={() => toggle("social")} testid="ice-sec-social">
        <RepeaterSection
          rows={profile.social_accounts || []}
          onChange={(rows) => setSection("social_accounts", rows)}
          fields={[
            { key: "platform", label: t("ice_soc_platform"), placeholder: "Instagram, X, Facebook…" },
            { key: "username", label: t("ice_soc_username"), placeholder: "@usuario" },
          ]}
          addLabel={t("ice_soc_add")}
          emptyLabel={t("ice_soc_empty")}
          testid="ice-social"
        />
        <p className="text-[11px] text-faded italic mt-2">{t("ice_b_no_creds")}</p>
      </Section>

      {/* Section 16 — Funeral wishes */}
      <Section id="funeral" icon={Flower} title={t("ice_sec_funeral")} hint={t("ice_sec_funeral_hint")}
        open={openSection === "funeral"} onToggle={() => toggle("funeral")} testid="ice-sec-funeral">
        <TextArea
          value={profile.funeral_wishes}
          onChange={(v) => setSection("funeral_wishes", v)}
          rows={5}
          placeholder={t("ice_funeral_ph")}
          testid="ice-funeral"
        />
        <p className="text-[11px] text-amber-300/80 italic mt-2">{t("ice_funeral_priority")}</p>
      </Section>

      {/* Section 17 — Additional notes */}
      <Section id="notes" icon={PencilLine} title={t("ice_sec_notes")} hint={t("ice_sec_notes_hint")}
        open={openSection === "notes"} onToggle={() => toggle("notes")} testid="ice-sec-notes">
        <TextArea
          value={profile.additional_notes}
          onChange={(v) => setSection("additional_notes", v)}
          rows={5}
          testid="ice-notes"
        />
      </Section>

      <div className="sticky bottom-3 z-10 flex justify-end pt-3">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className={cls("btn-gold shadow-lg", saving && "opacity-60")}
          data-testid="ice-editor-save"
        >
          <Save className="w-4 h-4" /> {saving ? (t("saving") || "…") : t("save")}
        </button>
      </div>
    </div>
  );
}
